import React from "react";
import { Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";

// Generic form field wrapper
interface FormFieldWrapperProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  description?: string;
  children: React.ReactNode;
}

export function FormFieldWrapper<T extends FieldValues>({
  control,
  name,
  label,
  description,
  children,
}: FormFieldWrapperProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>{children}</FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Text Input Field
interface TextInputFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  description?: string;
  type?: "text" | "email" | "password" | "tel";
  autoComplete?: string;
}

export function TextInputField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  type = "text",
  autoComplete,
}: TextInputFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type={type}
              placeholder={placeholder}
              autoComplete={autoComplete}
              {...field}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Number Input Field
interface NumberInputFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
}

export function NumberInputField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  min,
  max,
  step = 1,
}: NumberInputFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              placeholder={placeholder}
              min={min}
              max={max}
              step={step}
              {...field}
              value={field.value ?? 0}
              onChange={(e) => {
                const value = e.target.value;
                // Handle empty input or invalid numbers
                if (value === "" || value === null || value === undefined) {
                  field.onChange(0);
                } else {
                  const numValue = parseFloat(value);
                  field.onChange(isNaN(numValue) ? 0 : numValue);
                }
              }}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Price Input Field (specialized number field)
interface PriceInputFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  description?: string;
  currency?: string;
}

export function PriceInputField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder = "0.00",
  description,
  currency = "Rs",
}: PriceInputFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {currency}
              </span>
              <Input
                type="number"
                placeholder={placeholder}
                min="0"
                step="0.01"
                className="pl-8"
                {...field}
                value={field.value ?? 0}
                onChange={(e) => {
                  const raw = e.target.value;
                  // If empty, set to 0; otherwise ensure we have a valid number
                  if (raw === "" || raw === null || raw === undefined) {
                    field.onChange(0);
                  } else {
                    const numValue = Number(raw);
                    field.onChange(isNaN(numValue) ? 0 : numValue);
                  }
                }}
              />
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Select Field
interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  description?: string;
  options: SelectOption[];
}

export function SelectField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder = "Select an option",
  description,
  options,
}: SelectFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select
            onValueChange={field.onChange}
            value={field.value || ""}
            defaultValue={field.value || ""}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Textarea Field
interface TextareaFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  description?: string;
  rows?: number;
}

export function TextareaField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  rows = 3,
}: TextareaFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea placeholder={placeholder} rows={rows} {...field} />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Switch Field
interface SwitchFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  description?: string;
}

export function SwitchField<T extends FieldValues>({
  control,
  name,
  label,
  description,
}: SwitchFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <FormLabel className="text-base">{label}</FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
          </div>
          <FormControl>
            <Switch checked={field.value} onCheckedChange={field.onChange} />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

// Submit Button with loading state
interface FormSubmitButtonProps {
  isLoading?: boolean;
  loadingText?: string;
  children: React.ReactNode;
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  disabled?: boolean;
}

export function FormSubmitButton({
  isLoading = false,
  loadingText = "Saving...",
  children,
  variant = "default",
  size = "default",
  className,
  disabled = false,
}: FormSubmitButtonProps) {
  return (
    <Button
      type="submit"
      variant={variant}
      size={size}
      className={className}
      disabled={isLoading || disabled}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {loadingText}
        </>
      ) : (
        children
      )}
    </Button>
  );
}

// POS-specific role select options
export const roleOptions: SelectOption[] = [
  { value: "admin", label: "Administrator" },
  { value: "manager", label: "Manager" },
  { value: "counter", label: "Counter" },
];

// POS-specific status options
export const userStatusOptions: SelectOption[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const orderTypeOptions: SelectOption[] = [
  { value: "dine_in", label: "Dine In" },
  { value: "takeout", label: "Take Out" },
  { value: "delivery", label: "Delivery" },
];

export const tableStatusOptions: SelectOption[] = [
  { value: "available", label: "Available" },
  { value: "occupied", label: "Occupied" },
];

export const unitOptions: SelectOption[] = [
  { value: "kg", label: "Kilograms (kg)" },
  { value: "g", label: "Grams (g)" },
  { value: "lb", label: "Pounds (lb)" },
  { value: "oz", label: "Ounces (oz)" },
  { value: "l", label: "Liters (l)" },
  { value: "ml", label: "Milliliters (ml)" },
  { value: "dozen", label: "Dozen (12 pcs)" },
  { value: "pcs", label: "Pieces (pcs)" },
  { value: "boxes", label: "Boxes" },
  { value: "cans", label: "Cans" },
  { value: "bottles", label: "Bottles" },
  { value: "bags", label: "Bags" },
  { value: "rolls", label: "Rolls" },
  { value: "sheets", label: "Sheets" },
  { value: "units", label: "Units" },
  // Dental-specific units
  { value: "vials", label: "Vials" },
  { value: "ampoules", label: "Ampoules" },
  { value: "syringes", label: "Syringes" },
  { value: "cartridges", label: "Cartridges" },
  { value: "tubes", label: "Tubes" },
  { value: "jars", label: "Jars" },
  { value: "packets", label: "Packets" },
  { value: "strips", label: "Strips" },
  { value: "pads", label: "Pads" },
  { value: "swabs", label: "Swabs" },
  { value: "applicators", label: "Applicators" },
  { value: "tips", label: "Tips" },
  { value: "needles", label: "Needles" },
  { value: "burs", label: "Burs" },
  { value: "files", label: "Files" },
  { value: "gloves_pairs", label: "Gloves (Pairs)" },
  { value: "masks", label: "Masks" },
  { value: "gowns", label: "Gowns" },
  { value: "gauze", label: "Gauze" },
  { value: "cotton_rolls", label: "Cotton Rolls" },
  { value: "impression_trays", label: "Impression Trays" },
  { value: "crowns", label: "Crowns" },
  { value: "bridges", label: "Bridges" },
  { value: "implants", label: "Implants" },
  { value: "sutures", label: "Sutures" },
  { value: "packaged_sets", label: "Packaged Sets" },
];
